/*
  Faltu — variant sync (<faltu-variant-sync>)

  Keeps the ATC price, the price block and the gallery in step with the selected
  variant. The theme's product JS is encrypted, so rather than hook into it this
  watches the one thing every Shopify product form agrees on: the hidden
  input[name="id"] that carries the selected variant.

  It is a custom element rather than a one-shot script so that it also works in
  the quick-view modal: injected HTML never executes its <script> tags, but a
  custom element upgrades the moment it lands in the DOM.

  Everything is scoped to the element's own product container, so the modal copy
  and the page copy never write over each other.
*/
class FaltuVariantSync extends HTMLElement {
  connectedCallback () {
    const node = this.querySelector('[data-faltu-variant-data]')
    if (!node) return

    try { this.data = JSON.parse(node.textContent) } catch (e) { return }
    if (!this.data || !this.data.variants || !this.data.variants.length) return

    // the quick-view modal and the product page each have their own container
    this.root = this.closest('[data-quick-view-contents]') || this.closest('.product') || document
    this.form = this.closest('product-form') || this.root.querySelector('product-form') || this.root
    this.input = this.form.querySelector('input[name="id"]')
    if (!this.input) return

    this.last = null
    this.timers = []
    this.onChange = this.onChange.bind(this)
    this.syncSoon = this.syncSoon.bind(this)

    // the theme sets .value directly, which fires no event — watch the attribute
    this.observer = new MutationObserver(this.syncSoon)
    this.observer.observe(this.input, { attributes: true, attributeFilter: ['value'] })
    this.patchValueSetter()

    this.root.addEventListener('change', this.onChange)
    window.addEventListener('load', this.syncSoon)
    this.syncSoon()
  }

  disconnectedCallback () {
    if (this.observer) this.observer.disconnect()
    if (this.root) this.root.removeEventListener('change', this.onChange)
    window.removeEventListener('load', this.syncSoon)
    this.timers.forEach(clearTimeout)
    this.timers = []
  }

  patchValueSetter () {
    const desc = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')
    if (!desc || !desc.set || this.input.dataset.faltuPatched) return
    this.input.dataset.faltuPatched = 'true'
    const sync = this.syncSoon
    Object.defineProperty(this.input, 'value', {
      get () { return desc.get.call(this) },
      set (val) { desc.set.call(this, val); sync() },
      configurable: true
    })
  }

  onChange (e) {
    const t = e.target
    if (!t) return
    if (t === this.input || t.name === 'id' ||
        (t.hasAttribute && t.hasAttribute('data-option-position')) ||
        (t.closest && t.closest('.product-options'))) {
      this.syncSoon()
    }
  }

  /* Run now and again shortly after, so this settles AFTER the theme's own handler. */
  syncSoon () {
    this.sync()
    this.timers.push(setTimeout(() => this.sync(), 120))
    this.timers.push(setTimeout(() => this.sync(), 350))
  }

  variantById (id) {
    return this.data.variants.find((v) => String(v.id) === String(id)) || null
  }

  /* Replace a price element's text while leaving any visually-hidden label alone. */
  setMoney (el, text) {
    if (!el) return
    const kept = el.querySelector('.visually-hidden')
    el.textContent = ''
    if (kept) el.appendChild(kept)
    el.appendChild(document.createTextNode(text))
  }

  applyPrice (v) {
    this.root.querySelectorAll('.js-atc-price').forEach((el) => { el.textContent = v.price })

    this.root.querySelectorAll('.product-price').forEach((block) => {
      const final = block.querySelector('.product-price-final')
      const compare = block.querySelector('.product-price-compare')
      const saleBadge = block.querySelector('.product-sale-badge')
      const soldBadge = block.querySelector('.product-sold-out-badge')

      if (final) {
        this.setMoney(final, v.price)
        final.classList.toggle('product-price-final-sale', !!v.on_sale)
      }
      if (compare) {
        this.setMoney(compare, v.compare_at)
        compare.hidden = !v.on_sale
      }
      if (saleBadge) {
        saleBadge.hidden = !v.on_sale
        if (v.on_sale) {
          const pct = saleBadge.getAttribute('data-discount-type') === 'percentage'
          const label = (saleBadge.textContent || '').trim().split(/\s+/)[0]
          saleBadge.textContent = label + ' ' + (pct ? v.save_percent : v.save_amount)
        }
      }
      if (soldBadge) soldBadge.hidden = !!v.available
    })
  }

  applyAvailability (v) {
    this.root.querySelectorAll('.btn-atc, .btn-buy').forEach((btn) => { btn.disabled = !v.available })
    this.root.querySelectorAll('.btn-atc').forEach((btn) => {
      const priceEl = btn.querySelector('.js-atc-price')
      if (!v.available && !priceEl) { btn.textContent = this.data.sold_out; return }
      if (v.available && !priceEl && btn.textContent.trim() === this.data.sold_out) {
        btn.textContent = this.data.add_to_cart
      }
    })
  }

  applyGallery (v) {
    if (!v.media_id) return

    // Desktop editorial grid — bring the variant's photo into view.
    const cell = this.root.querySelector('.hf-media-cell[data-media-id="' + v.media_id + '"]')
    if (cell && cell.style.display !== 'none' && cell.offsetParent !== null) {
      const rect = cell.getBoundingClientRect()
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        cell.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    }

    // Mobile / modal — drive the swiper through its own thumbnail so the
    // theme's slider instance stays the single source of truth.
    const thumbs = this.root.querySelectorAll('.swiper-thumbs .swiper-slide')
    if (thumbs.length && v.media_position) {
      const target = thumbs[v.media_position - 1]
      if (target && target.offsetParent !== null && !target.classList.contains('swiper-slide-thumb-active')) {
        target.click()
      }
    }
  }

  sync () {
    if (!this.input || !this.isConnected) return
    const id = this.input.value
    if (!id) return
    const v = this.variantById(id)
    if (!v) return
    this.applyPrice(v)
    this.applyAvailability(v)
    if (String(id) !== String(this.last)) {
      this.applyGallery(v)
      this.last = id
    }
  }
}

if (!customElements.get('faltu-variant-sync')) customElements.define('faltu-variant-sync', FaltuVariantSync)
